"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { useNonogramContext } from "./nonogram_context";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import "./nonogram.css";
import { CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { post } from "../../../../lib/api";
import { NonogramViewer } from "../Generator From Image/nonogram_viewer";

const NonogramGenerator = ({
                             game_id,
                             initialNonogramDataBW,
                             initialNonogramDataColor,
                             initialChallengeName,
                             onUpdate,
                           }) => {
  const [imageFile, setImageFile] = useState(null);
  const [bwImage, setBWImage] = useState(null);
  const [colorImage, setColorImage] = useState(null);
  const [tolerance, setTolerance] = useState(128);
  const [challengeName, setChallengeName] = useState(initialChallengeName || "");
  const [activeButton, setActiveButton] = useState("5");
  const fileInputRef = useRef(null);
  const [invertBW, setInvertBW] = useState(false);
  const [showViewer, setShowViewer] = useState(false);

  // 🆕 Added Lennard checkbox state
  const [isLennard, setIsLennard] = useState(false);

  const hiddenCanvasRef = useRef(null);
  const colorCanvasRef = useRef(null);
  const bwCanvasRef = useRef(null);

  const {
    bwValues,
    setBWValues,
    rgbValues,
    setRGBValues,
    pixelArtSize,
    setPixelArtSize,
    isValid,
    setNewUploadedNono,
  } = useNonogramContext();

  const cellSize = 20;
  const displaySize = pixelArtSize * cellSize;

  // Calculate Otsu's threshold for automatic B/W detection
  function calculateOtsuThreshold(imageData) {
    const histogram = new Array(256).fill(0);
    const total = imageData.data.length / 4;

    // Build histogram
    for (let i = 0; i < imageData.data.length; i += 4) {
      const gray = Math.round(
          (imageData.data[i] + imageData.data[i + 1] + imageData.data[i + 2]) / 3
      );
      histogram[gray]++;
    }

    let sum = 0;
    for (let i = 0; i < 256; i++) {
      sum += i * histogram[i];
    }

    let sumB = 0;
    let wB = 0;
    let wF = 0;
    let maxVariance = 0;
    let threshold = 0;

    for (let i = 0; i < 256; i++) {
      wB += histogram[i];
      if (wB === 0) continue;

      wF = total - wB;
      if (wF === 0) break;

      sumB += i * histogram[i];
      const mB = sumB / wB;
      const mF = (sum - sumB) / wF;
      const variance = wB * wF * (mB - mF) * (mB - mF);

      if (variance > maxVariance) {
        maxVariance = variance;
        threshold = i;
      }
    }

    return threshold;
  }

  useEffect(() => {
    if (initialNonogramDataBW) {
      loadNonogramDataBW(initialNonogramDataBW);
    } else {
      setPixelArtSize(15);
      setActiveButton("15");
    }
  }, [initialNonogramDataBW]);

  function loadNonogramDataBW(data) {
    const bwArray = data.split(",").map(Number);
    const size = Math.sqrt(bwArray.length);

    const tmpCanvas = document.createElement("canvas");
    tmpCanvas.width = size;
    tmpCanvas.height = size;
    const ctx = tmpCanvas.getContext("2d");
    const imageData = ctx.createImageData(size, size);

    for (let i = 0; i < bwArray.length; i++) {
      const isBlack = bwArray[i] === 1 ? 0 : 255;
      const idx = i * 4;
      imageData.data[idx + 0] = isBlack;
      imageData.data[idx + 1] = isBlack;
      imageData.data[idx + 2] = isBlack;
      imageData.data[idx + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);

    const dataURL = tmpCanvas.toDataURL();
    const img = new Image();
    img.onload = () => {
      setBWImage(img);
      setPixelArtSize(size);
      setActiveButton(size.toString());
      setBWValues(bwArray);
      setTolerance(128);
    };
    img.src = dataURL;
  }

  useEffect(() => {
    if (initialNonogramDataColor) {
      loadNonogramDataColor(initialNonogramDataColor);
    }
  }, [initialNonogramDataColor]);

  function loadNonogramDataColor(data) {
    const rgbArray = data.split(",").map(Number);
    const size = Math.sqrt(rgbArray.length / 3);

    const tmpCanvas = document.createElement("canvas");
    tmpCanvas.width = size;
    tmpCanvas.height = size;
    const ctx = tmpCanvas.getContext("2d");
    const imageData = ctx.createImageData(size, size);

    for (let i = 0; i < size * size; i++) {
      const dataIndex = i * 3;
      const canvasIndex = i * 4;
      imageData.data[canvasIndex + 0] = rgbArray[dataIndex + 0];
      imageData.data[canvasIndex + 1] = rgbArray[dataIndex + 1];
      imageData.data[canvasIndex + 2] = rgbArray[dataIndex + 2];
      imageData.data[canvasIndex + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);

    const dataURL = tmpCanvas.toDataURL();
    const img = new Image();
    img.onload = () => {
      setColorImage(img);
      setRGBValues(
          rgbArray
              .map((val, idx) =>
                  idx % 3 === 0 ? [rgbArray[idx], rgbArray[idx + 1], rgbArray[idx + 2]] : null
              )
              .filter(Boolean)
      );
    };
    img.src = dataURL;
  }

  useEffect(() => {
    setChallengeName(initialChallengeName || "");
  }, [initialChallengeName]);

  useEffect(() => {
    if (!bwImage || !pixelArtSize) return;

    const hiddenCanvas = hiddenCanvasRef.current;
    hiddenCanvas.width = pixelArtSize;
    hiddenCanvas.height = pixelArtSize;
    const hiddenCtx = hiddenCanvas.getContext("2d");

    hiddenCtx.imageSmoothingEnabled = false;
    hiddenCtx.drawImage(bwImage, 0, 0, pixelArtSize, pixelArtSize);

    const bwCanvas = bwCanvasRef.current;
    bwCanvas.width = displaySize;
    bwCanvas.height = displaySize;
    const bwCtx = bwCanvas.getContext("2d");
    bwCtx.imageSmoothingEnabled = false;
    bwCtx.drawImage(hiddenCanvas, 0, 0, displaySize, displaySize);

    convertToBlackAndWhite();
  }, [bwImage, pixelArtSize, tolerance, invertBW]);

  function convertToBlackAndWhite() {
    if (!bwImage) return;

    const hiddenCanvas = hiddenCanvasRef.current;
    const hiddenCtx = hiddenCanvas.getContext("2d");
    const sourceImageData = hiddenCtx.getImageData(0, 0, pixelArtSize, pixelArtSize);

    const bwCanvas = bwCanvasRef.current;
    const bwCtx = bwCanvas.getContext("2d");
    const bwImageData = bwCtx.createImageData(displaySize, displaySize);

    const newBW = [];

    for (let y = 0; y < pixelArtSize; y++) {
      for (let x = 0; x < pixelArtSize; x++) {
        const idx = (y * pixelArtSize + x) * 4;
        const gray = Math.round(
            0.299 * sourceImageData.data[idx] +
            0.587 * sourceImageData.data[idx + 1] +
            0.114 * sourceImageData.data[idx + 2]
        );

        let isBlack = gray < tolerance;
        if (invertBW) isBlack = !isBlack;

        const val = isBlack ? 0 : 255;
        newBW.push(isBlack ? 1 : 0);

        for (let dy = 0; dy < cellSize; dy++) {
          for (let dx = 0; dx < cellSize; dx++) {
            const displayX = x * cellSize + dx;
            const displayY = y * cellSize + dy;
            const displayIdx = (displayY * displaySize + displayX) * 4;
            bwImageData.data[displayIdx + 0] = val;
            bwImageData.data[displayIdx + 1] = val;
            bwImageData.data[displayIdx + 2] = val;
            bwImageData.data[displayIdx + 3] = 255;
          }
        }
      }
    }

    bwCtx.putImageData(bwImageData, 0, 0);
    setBWValues(newBW);
  }

  useEffect(() => {
    if (!colorImage || !pixelArtSize) return;
    const colorCanvas = colorCanvasRef.current;
    colorCanvas.width = displaySize;
    colorCanvas.height = displaySize;
    const ctx = colorCanvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(colorImage, 0, 0, displaySize, displaySize);
  }, [colorImage, pixelArtSize]);

  function isImgFile(file) {
    if (!file) return false;
    if (!file.type.startsWith("image/")) {
      toast.error("The selected file is not an image file!");
      return false;
    }
    return true;
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isImgFile(file)) return;
    setImageFile(file);
  }

  function handleImageChange(file) {
    const reader = new FileReader();
    reader.onload = (evt) => {
      const img = new Image();
      img.onload = () => {
        setBWImage(img);
        setColorImage(img);

        const tmpCanvas = document.createElement("canvas");
        tmpCanvas.width = pixelArtSize;
        tmpCanvas.height = pixelArtSize;
        const tmpCtx = tmpCanvas.getContext("2d");
        tmpCtx.imageSmoothingEnabled = false;
        tmpCtx.drawImage(img, 0, 0, pixelArtSize, pixelArtSize);

        const imageData = tmpCtx.getImageData(0, 0, pixelArtSize, pixelArtSize);

        const autoThreshold = calculateOtsuThreshold(imageData);
        setTolerance(autoThreshold);

        const newRGB = [];
        for (let y = 0; y < pixelArtSize; y++) {
          for (let x = 0; x < pixelArtSize; x++) {
            const idx = (y * pixelArtSize + x) * 4;
            newRGB.push([
              imageData.data[idx],
              imageData.data[idx + 1],
              imageData.data[idx + 2],
            ]);
          }
        }
        setRGBValues(newRGB);
      };
      img.src = evt.target.result;
    };
    reader.readAsDataURL(file);
    setShowViewer(true);
  }

  useEffect(() => {
    if (imageFile) {
      handleImageChange(imageFile);
    }
  }, [imageFile, pixelArtSize]);

  const togglePixelColor = useCallback(
      (e) => {
        const bwCanvas = bwCanvasRef.current;
        const bwCtx = bwCanvas.getContext("2d");
        const rect = bwCanvas.getBoundingClientRect();

        const xCell = Math.floor((e.clientX - rect.left) / cellSize);
        const yCell = Math.floor((e.clientY - rect.top) / cellSize);

        if (xCell < 0 || xCell >= pixelArtSize || yCell < 0 || yCell >= pixelArtSize) return;

        const idx = yCell * pixelArtSize + xCell;
        const updatedBW = [...bwValues];
        updatedBW[idx] = updatedBW[idx] === 1 ? 0 : 1;
        setBWValues(updatedBW);

        const newVal = updatedBW[idx] === 1 ? 0 : 255;
        const imageData = bwCtx.createImageData(cellSize, cellSize);
        for (let i = 0; i < imageData.data.length; i += 4) {
          imageData.data[i + 0] = newVal;
          imageData.data[i + 1] = newVal;
          imageData.data[i + 2] = newVal;
          imageData.data[i + 3] = 255;
        }
        bwCtx.putImageData(imageData, xCell * cellSize, yCell * cellSize);
      },
      [cellSize, pixelArtSize, bwValues, setBWValues]
  );

  useEffect(() => {
    const bwCanvas = bwCanvasRef.current;
    bwCanvas.addEventListener("click", togglePixelColor);
    return () => bwCanvas.removeEventListener("click", togglePixelColor);
  }, [togglePixelColor]);

  async function updateChallenge(game_id, challenge_name, data, variant_id) {
    const variantNames = {
      1: "monochrome",
      2: "color",
      3: "Lennard",
    };

    const body = {
      method: "addChallenge",
      game_id,
      challenge_id: challenge_name,
      data,
      variant_id,
      variant_name: variantNames[variant_id],
    };
    try {
      await post(body, "/challenges");
    } catch (error) {
      console.error("Error updating challenge:", error);
      throw error;
    }
  }

  async function handlePostNonogram() {
    if (!isValid || !bwImage || challengeName.trim() === "") {
      toast.error("Puzzle invalid, no image loaded, or no challenge name!");
      return;
    }

    try {
      await updateChallenge(game_id, challengeName, bwValues.toString(), 1);
      const colorArrayAsNumbers = rgbValues.flat();
      await updateChallenge(game_id, challengeName, colorArrayAsNumbers.toString(), 2);

      // 🆕 Add Lennard upload if checked
      if (isLennard) {
        await updateChallenge(game_id, challengeName, "Lennard APPROVED!!", 3);
      }

      setNewUploadedNono({
        name: challengeName,
        data: bwValues.toString(),
        date: null,
        gameid: game_id,
      });
      toast.success("Nonogram successfully uploaded!");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Error uploading Nonogram!");
    }
  }

  function handleButtonClick(sizeStr) {
    const size = Number.parseInt(sizeStr, 10);
    setPixelArtSize(size);
    setActiveButton(sizeStr);
  }

  return (
      <div className="nonogram-generator-container space-y-3">
        <NonogramViewer showViewer={showViewer} setShowViewer={setShowViewer} />

        <div className="upload-file-container">
          <Input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input"
              ref={fileInputRef}
              style={{ display: "none" }}
          />
          <Button
              className="bg-purple-800 hover:bg-purple-950 dark:bg-purple-800 dark:text-white text-white"
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
          >
            Upload file
          </Button>
        </div>

        <div className="gap-3 flex justify-center items-center">
          <Label>Pixel Art Size: </Label>
          <Button
              className={`w-4 h-6 bg-purple-800 dark:bg-purple-800 dark:text-white hover:bg-purple-950 ${
                  activeButton === "5" ? "active" : ""
              }`}
              onClick={() => handleButtonClick("5")}
          >
            5
          </Button>
          <Button
              className={`w-4 h-6 bg-purple-800 dark:bg-purple-800 dark:text-white hover:bg-purple-950 ${
                  activeButton === "10" ? "active" : ""
              }`}
              onClick={() => handleButtonClick("10")}
          >
            10
          </Button>
          <Button
              className={`w-4 h-6 bg-purple-800 dark:bg-purple-800 dark:text-white hover:bg-purple-950 ${
                  activeButton === "15" ? "active" : ""
              }`}
              onClick={() => handleButtonClick("15")}
          >
            15
          </Button>
        </div>

        <div className="flex items-center space-x-2 mt-3 mb-2 justify-center">
          <Checkbox
              className="shadow-none data-[state=checked]:bg-purple-700 dark:data-[state=checked]:bg-purple-700 text-white"
              id="invert-bw"
              checked={invertBW}
              onCheckedChange={setInvertBW}
          />
          <Label htmlFor="invert-bw" className="text-sm font-medium leading-none">
            Invert colors
          </Label>
        </div>

        <div className="flex flex-col items-center space-y-2 mt-3 mb-2">
          <Label htmlFor="tolerance-slider" className="text-sm font-medium leading-none">
            B/W Threshold: {tolerance}
          </Label>
          <Input
              id="tolerance-slider"
              type="range"
              min="0"
              max="255"
              step="1"
              value={tolerance}
              onChange={(e) => setTolerance(Number(e.target.value))}
              className="w-64"
          />
          <p className="text-xs text-gray-500">Lower values = more black pixels</p>
        </div>

        <div className="canvas-display" style={{display: "flex", gap: "1rem"}}>
          <canvas ref={hiddenCanvasRef} style={{display: "none"}}></canvas>
          <canvas ref={colorCanvasRef}></canvas>
          <canvas ref={bwCanvasRef}></canvas>
        </div>

        <div className="flex flex-col items-center mt-4 space-y-2">
          {/* Name input and button on the same row */}
          <div className="flex items-center gap-2">
            <Input
                type="text"
                placeholder="Enter challenge name"
                value={challengeName}
                onChange={(e) => setChallengeName(e.target.value)}
                className="w-60"
            />
            <Button
                className="bg-purple-800 hover:bg-purple-950 dark:bg-purple-800 dark:text-white text-white"
                onClick={handlePostNonogram}
            >
              Post Nonogram
            </Button>
          </div>

          {/* Lennard checkbox below */}
          <div className="flex items-center gap-2">
            <Checkbox
                id="lennard-upload"
                checked={isLennard}
                onCheckedChange={setIsLennard}
                className="shadow-none data-[state=checked]:bg-purple-700 dark:data-[state=checked]:bg-purple-700 text-white"
            />
            <Label htmlFor="lennard-upload" className="text-sm font-medium leading-none">
              Lennard approved
            </Label>
          </div>
        </div>
      </div>
  );
};

export default NonogramGenerator;
